import { DateTime } from 'luxon';

type HubEventDateFields = {
  start_date_time: string;
  end_date_time: string;
  timezone: string;
  is_all_day?: boolean | null;
};

function formatHubEventCompactTime(dateTime: DateTime): string {
  if (!dateTime.isValid) {
    return '';
  }
  const hour = dateTime.hour % 12 || 12;
  const ampm = dateTime.hour < 12 ? 'am' : 'pm';
  if (dateTime.minute === 0) {
    return `${hour}${ampm}`;
  }
  const minutes = dateTime.minute.toString().padStart(2, '0');
  return `${hour}:${minutes}${ampm}`;
}

export function formatHubEventDateTimeRange(
  event: HubEventDateFields,
  locale: string,
  allDayLabel: string,
): string {
  const zone = event.timezone;
  const start = DateTime.fromISO(event.start_date_time, { zone }).setLocale(
    locale,
  );
  const end = DateTime.fromISO(event.end_date_time, { zone }).setLocale(locale);

  if (!start.isValid) {
    return '';
  }

  const datePart = start.toFormat('EEE, MMM d yyyy');

  if (event.is_all_day === true) {
    return `${datePart} • ${allDayLabel}`;
  }

  const timezoneLabel = start.offsetNameShort || start.toFormat('ZZZZ');
  const startTime = formatHubEventCompactTime(start);

  if (!end.isValid) {
    return `${datePart} • ${startTime} ${timezoneLabel}`.trim();
  }

  const endTime = formatHubEventCompactTime(end);
  return `${datePart} • ${startTime} - ${endTime} ${timezoneLabel}`.trim();
}

export function formatHubEventCreatedAt(
  iso: string,
  timezone: string,
  locale: string,
): string {
  const dateTime = DateTime.fromISO(iso, { zone: timezone }).setLocale(locale);
  if (!dateTime.isValid) {
    return '';
  }
  return `${dateTime.toFormat('MMM d, yyyy')} ${formatHubEventCompactTime(dateTime)}`;
}

/**
 * Returns the local date string (YYYY-MM-DD) for an all-day Hub event.
 * Hub sends UTC datetime strings for all-day events; without timezone conversion
 * the UTC date can fall on the previous calendar day in negative-offset timezones.
 */
export function hubAllDayEventToCalendarDate(
  dateTime: string,
  timezone: string,
): string {
  return DateTime.fromISO(dateTime, { zone: timezone }).toISODate() ?? dateTime;
}

export function formatHubEventInlineTime(args: {
  startDateTime: string;
  timezone: string;
  isAllDay: boolean;
  locale: string;
  allDayLabel: string;
}): string {
  if (args.isAllDay) {
    return args.allDayLabel;
  }
  const dateTime = DateTime.fromISO(args.startDateTime, {
    zone: args.timezone,
  }).setLocale(args.locale);
  return formatHubEventCompactTime(dateTime);
}
